import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Star, Search, Plus } from 'lucide-react';
import { permission } from '../../../../config/permission';
import { useUserContext } from '../../../../contexts/UserContext';
import { updateSpecificAEMAdobeIO } from '../../../../utils/APIs';
import ErrorAlert from '../../../../components/UI/Alert/ErrorAlert';
import { toast } from "react-toastify";
import Loader from '../../../../components/UI/Loader/Loader';
import { formatDate } from '../../../../utils/converter';
import { useContentfulContext } from '../../../../contexts/ContentfulContext';
import { getAgGridSortStorageKey } from '../../../../components/UI/AgGrid';
import DeleteAEMAdobeIOModal from '../../../../components/AEMAdobeIO/DeleteIntegrationModal';
import { Card, Button, Input, AgTableCard } from '../../../../components/UI';

const AEMAdobeIO = () => {
  const navigate = useNavigate();
  const { userData, checkPermission, updateAEMAdobeIOIntegrations, loading } = useUserContext();
  const { enums, content } = useContentfulContext();

  const [deleteIntegration, setDeleteIntegration] = useState(false);
  const [aemAdobeIOData, setAEMAdobeIOData] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [handleId, setHandleId] = useState("");
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setAEMAdobeIOData(userData.aemAdobeIOs || []);
  }, [userData.aemAdobeIOs]);

  const [gridApi, setGridApi] = useState(null);

  const onFilterTextBoxChanged = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (gridApi) {
      gridApi.setFilterModel({
        aem_adobe_io_name: val ? { type: 'contains', filter: val.toLowerCase() } : null
      });
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    if (gridApi) {
      gridApi.setFilterModel({});
    }
  };

  const handleSetAsDefault = async (id) => {
    try {
      setActionLoading(true);
      await updateSpecificAEMAdobeIO(
        userData.token,
        userData.selectedOrganization.org_token,
        { id, bodyData: { default: true } }
      );
      toast.success(content.AEM_ADOBE_IO_CONTENT.success_default_msg);
      updateAEMAdobeIOIntegrations();
    } catch (err) {
      setError(err?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setActionLoading(false);
    }
  };

  const colDefs = [
    {
      headerName: content.AEM_ADOBE_IO_CONTENT.integration_table_headers.aem_adobe_io_name,
      field: "aem_adobe_io_name",
      cellRenderer: ({ data }) => (
        <button
          type="button"
          className="font-medium text-primary hover:text-primary-hover hover:underline text-left"
          onClick={() => navigate(`/setup/new-aem-adobe-io/${data._id}`)}
        >
          {data.aem_adobe_io_name}
        </button>
      ),
      filter: true,
      sortable: true,
      suppressMovable: true,
    },
    {
      headerName: content.AEM_ADOBE_IO_CONTENT.integration_table_headers.imsEndpoint,
      field: "imsEndpoint",
      filter: true,
      sortable: true,
      suppressMovable: true,
      cellRenderer: ({ value }) => (
        <span className="font-mono text-xs text-stride-muted">{value || "—"}</span>
      ),
    },
    {
      headerName: content.AEM_ADOBE_IO_CONTENT.integration_table_headers.metascopes,
      field: "metascopes",
      sortable: false,
      cellRenderer: ({ value }) => (
        <span className="stride-badge-pill bg-slate-100 text-slate-700 text-xs font-mono">{value || "—"}</span>
      ),
      suppressMovable: true,
    },
    {
      headerName: content.AEM_ADOBE_IO_CONTENT.integration_table_headers.email,
      field: "email",
      sortable: false,
      suppressMovable: true,
      cellRenderer: ({ value }) => (
        <span className="text-stride-body text-xs">{value || "—"}</span>
      ),
    },
    {
      headerName: content.AEM_ADOBE_IO_CONTENT.integration_table_headers.org,
      field: "org",
      sortable: false,
      suppressMovable: true,
      cellRenderer: ({ value }) => (
        <span className="font-mono text-xs text-stride-muted">{value || "—"}</span>
      ),
    },
    {
      headerName: content.AEM_ADOBE_IO_CONTENT.integration_table_headers.certificateExpirationDate,
      field: "certificateExpirationDate",
      sortable: false,
      suppressMovable: true,
      cellRenderer: ({ value }) => (
        <span className="text-stride-muted text-xs">{value ? formatDate(value) : "—"}</span>
      ),
    },
    {
      headerName: content.AEM_ADOBE_IO_CONTENT.integration_table_headers.default,
      field: "default",
      sortable: false,
      suppressMovable: true,
      cellRenderer: ({ data }) => {
        const isDefault = data.default === true || data.default === "true";
        return isDefault ? (
          <span className="stride-badge-pill bg-emerald-50 text-emerald-700 font-semibold text-xs">
            Default
          </span>
        ) : (
          <span className="text-stride-muted text-xs">—</span>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ({ data }) => {
        const canEdit = checkPermission(permission.EDIT_AEM_ADOBE_IO);
        const canDelete = checkPermission(permission.DELETE_AEM_ADOBE_IO);
        const canSetDefault = checkPermission(permission.EDIT_AEM_ADOBE_IO_OP_EDIT_DEFAULT);
        const isDefault = data.default === true || data.default === "true";

        return (
          <div className="stride-table-actions">
            {canEdit && (
              <button
                type="button"
                title="Edit Integration"
                className="stride-table-btn stride-table-btn-edit"
                onClick={() => navigate(`/setup/new-aem-adobe-io/${data._id}`)}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {canSetDefault && !isDefault && (
              <button
                type="button"
                title="Set as default"
                className="stride-table-btn hover:text-amber-600 hover:border-amber-300"
                onClick={() => handleSetAsDefault(data._id)}
              >
                <Star className="w-3.5 h-3.5 text-amber-500" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                title="Delete Integration"
                className="stride-table-btn stride-table-btn-delete"
                onClick={() => {
                  setHandleId(data._id);
                  setDeleteIntegration(true);
                }}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      },
      width: 100,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
    },
  ];

  const onGridReady = (params) => {
    setGridApi(params.api);

    const storageKey = getAgGridSortStorageKey("aem-adobe-io-table");
    const sortModel = JSON.parse(localStorage.getItem(storageKey));

    if (!sortModel || sortModel?.length === 0) {
      params.columnApi.applyColumnState({
        state: [{ colId: "aem_adobe_io_name", sort: "asc" }],
        applyOrder: false,
      });
    }
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 40,
      columnLimits: [
        { colId: "aem_adobe_io_name", minWidth: 220 },
        { colId: "imsEndpoint", minWidth: 160 },
        { colId: "metascopes", minWidth: 140 },
        { colId: "email", minWidth: 180 },
        { colId: "org", minWidth: 140 },
        { colId: "certificateExpirationDate", minWidth: 140 },
        { colId: "default", minWidth: 100 },
        { colId: "actions", minWidth: 90 },
      ],
    };
  }, []);

  return (
    <main id="main" className="stride-page-shell flex flex-col space-y-6">
      {error && <ErrorAlert timeout={3000}>{error}</ErrorAlert>}
      {/* Header */}
      <div>
        <span className="stride-eyebrow">Setup / Integrations</span>
        <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
          {content.AEM_ADOBE_IO_CONTENT.title}
        </h1>
        <p className="text-sm text-stride-muted mt-1">
          {content.AEM_ADOBE_IO_CONTENT.description}
        </p>
      </div>

      {/* Empty State */}
      {!loading && (!aemAdobeIOData || aemAdobeIOData.length === 0) ? (
        <Card className="text-center p-12 max-w-lg mx-auto">
          <p className="text-base text-stride-heading font-medium mb-4">
            {content.AEM_ADOBE_IO_CONTENT.error.FE133}
          </p>
          {checkPermission(permission.CREATE_AEM_ADOBE_IO) && (
            <Button
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => navigate("/setup/new-aem-adobe-io")}
            >
              {content.AEM_ADOBE_IO_CONTENT.new_cta_text}
            </Button>
          )}
        </Card>
      ) : (
        <AgTableCard
          header={
            <>
              <div className="w-full md:w-80">
                <Input
                  placeholder="Search integration name..."
                  value={searchTerm}
                  onChange={onFilterTextBoxChanged}
                  prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                  clearable
                  onClear={handleClearSearch}
                />
              </div>

              {checkPermission(permission.CREATE_AEM_ADOBE_IO) && (
                <Button
                  variant="primary"
                  icon={<Plus className="w-4 h-4" />}
                  onClick={() => navigate("/setup/new-aem-adobe-io")}
                >
                  {content.AEM_ADOBE_IO_CONTENT.new_cta_text}
                </Button>
              )}
            </>
          }
          tableId="aem-adobe-io-table"
          loading={loading}
          rowData={aemAdobeIOData}
          columnDefs={colDefs}
          autoSizeStrategy={autoSizeStrategy}
          onGridReady={onGridReady}
          rowSelection="multiple"
          suppressRowClickSelection
          pagination={true}
          paginationPageSize={content.AEM_ADOBE_IO_CONTENT.default_page_size}
          paginationPageSizeSelector={enums?.PAGINATION_PAGE_SIZE_SELECTOR}
        />
      )}

      {actionLoading && <Loader />}

      {deleteIntegration && (
        <DeleteAEMAdobeIOModal
          id={handleId}
          setDeleteIntegration={setDeleteIntegration}
          setActionLoading={setActionLoading}
          refreshAEMAdobeIOData={updateAEMAdobeIOIntegrations}
        />
      )}
    </main>
  );
};

export default AEMAdobeIO;
